import { combineReducers } from '@reduxjs/toolkit';
import {
    DossierApi,
    FormaliteApi,
    Configuration as FormalitesConfiguration,
    ConfigurationParameters as FormaliteConfigurationParameters,
    CollectionDeDossierApi,
    FichiersDossierApi,
    FormalisteApi,
    StatutApi,
    DossierOpenSearchApi,
    PrestationApi,
    MessageApi,
    TypeDeDocumentDesDossiersApi,
    TypeDeDocumentApi,
    ObjectGroupsApi,
    PrestationArticlesApi,
    AcomptesApi,
    ReglementsApi,
    ProcdureDeValidationDesDossiersApi,
    ValideursDesDossiersApi,
    NotesApi,
    GreffeApi,
    CollectionOpenSearchApi,
    TachesEtRappelsDesDossierApi,
    ActionLoggerApi,
    DossiersDeSignatureLectroniqueApi,
    DocumentGedApi,
    FormaliteArticleOpenSearchApi,
} from '@europrocurement/l2d-domain/openApi/ApiFormalite';
import { createDossierFormaliteSlice } from './slices/dossierFormaliteSlice';
import { createDossierOpenSearchSlice } from './slices/dossierOpenSearchSlice';
import { createDossierOpenSearchPrescriberSlice } from './slices/dossierOpenSearchPrescriberSlice';
import { createFormaliteSlice } from './slices/formalite';
import { createCategoriesFormaliteSlice } from './slices/categoriesFormalite';
import { createLegalStatusSlice } from './slices/legalStatusSlice';
import { createFormaliteDocumentSlice } from './slices/documentSlice';
import { createFormalisteSlice } from './slices/formalisteSlice';
import { createDossierStepSlice } from './slices/dossierStepSlice';
import {
    CATEGORY_SLICE_NAME,
    DOSSIER_OBJECT_GROUP_SLICE_NAME,
    DOSSIER_STEP_SLICE_NAME,
    DOSSIER_VALIDATOR_SLICE_NAME,
    DOSSIERS_FORMALITES_SLICE_NAME,
    DOSSIERS_OPEN_SEARCH_OBJECT_GROUP_SLICE_NAME,
    DOSSIERS_OPEN_SEARCH_SLICE_NAME,
    DOSSIERS_OPEN_SEARCH_PRESCRIBER_SLICE_NAME,
    ORDERS_OPEN_SEARCH_SLICE_NAME,
    FORMALISTE_SLICE_NAME,
    FORMALITE_DOCUMENT_SLICE_NAME,
    FORMALITES_REDUCER_NAME,
    FORMALITES_SLICE_NAME,
    LEGAL_STATUS_SLICE_NAME,
    ORDERS_SLICE_NAME,
    STATUS_SLICE_NAME,
    DOSSIER_TACHES_SLICE_NAME,
    DOSSIER_LOG_ACTION_SLICE_NAME,
} from './constants';
import { type FormalityStateType } from './types';
import { buildFormalityModel } from './Models';
import { createStatusSlice } from './slices/statusSlice';
import { createDossierObjectGroupSlice } from './slices/dossierObjectGroupSlice';
import { createDossierOpenSearchObjectGroupSlice } from './slices/dossierOpenSearchObjectGroupSlice';
import { createOrderSlice } from './slices/orderSlice';
import { createDossierValidatorSlice } from './slices/dossierValidatorSlice';
import { createOrderOpenSearchSlice } from './slices/orderOpenSearchSlice';
import { createDossierTachesSlice } from './slices/dossierTachesSlice';
import { createDossierLogActionSlice } from './slices/dossierLogActionSlice';

export { type FormaliteConfigurationParameters };

export function createOrderReducer(configuration: FormaliteConfigurationParameters) {
    const { orderSlice, orderDataSourcesThunks } = createOrderSlice(configuration);
    return { orderSlice, orderDataSourcesThunks };
}

export function createDossierFormaliteReducer(configuration: FormaliteConfigurationParameters) {
    const { dossierFormaliteSlice, dossierFormaliteDataSourcesThunks } =
        createDossierFormaliteSlice(configuration);
    return { dossierFormaliteSlice, dossierFormaliteDataSourcesThunks };
}

export function createDossierOpenSearchReducer(configuration: FormaliteConfigurationParameters) {
    const { dossierOpenSearchSlice, dossierOpenSearchDataSourcesThunks } =
        createDossierOpenSearchSlice(configuration);
    return { dossierOpenSearchSlice, dossierOpenSearchDataSourcesThunks };
}

export function createDossierOpenSearchPrescriberReducer(
    configuration: FormaliteConfigurationParameters,
) {
    const { dossierOpenSearchPrescriberSlice, dossierOpenSearchPrescriberDataSourcesThunks } =
        createDossierOpenSearchPrescriberSlice(configuration);
    return { dossierOpenSearchPrescriberSlice, dossierOpenSearchPrescriberDataSourcesThunks };
}

export function createOrderrOpenSearchReducer(configuration: FormaliteConfigurationParameters) {
    const { orderOpenSearchSlice, orderOpenSearchDataSourcesThunks } =
        createOrderOpenSearchSlice(configuration);
    return { orderOpenSearchSlice, orderOpenSearchDataSourcesThunks };
}

export function createFormaliteReducer(configuration: FormaliteConfigurationParameters) {
    const { formaliteSlice, formaliteDataSourcesThunks } = createFormaliteSlice(configuration);
    return { formaliteSlice, formaliteDataSourcesThunks };
}

export function createCategoriesFormaliteReducer(configuration: FormaliteConfigurationParameters) {
    const { categoryFormaliteSlice, categoryFormaliteDataSourcesThunks } =
        createCategoriesFormaliteSlice(configuration);
    return { categoryFormaliteSlice, categoryFormaliteDataSourcesThunks };
}

export function createLegalStatusReducer(configuration: FormaliteConfigurationParameters) {
    const { legalStatusSlice, legalStatusDataSourcesThunks } =
        createLegalStatusSlice(configuration);
    return { legalStatusSlice, legalStatusDataSourcesThunks };
}

export function createFormaliteDocumentReducer(configuration: FormaliteConfigurationParameters) {
    const { formaliteDocumentSlice, formaliteDocumentDataSourcesThunks } =
        createFormaliteDocumentSlice(configuration);
    return { formaliteDocumentSlice, formaliteDocumentDataSourcesThunks };
}

export function createFormalisteReducer(configuration: FormaliteConfigurationParameters) {
    const { formalisteSlice, formalisteDataSourcesThunks } = createFormalisteSlice(configuration);
    return { formalisteSlice, formalisteDataSourcesThunks };
}

export function createDossierStepReducer(configuration: FormaliteConfigurationParameters) {
    const { dossierStepSlice, dossierStepDataSourcesThunks } =
        createDossierStepSlice(configuration);
    return { dossierStepSlice, dossierStepDataSourcesThunks };
}

export function createDossierObjectGroupReducer(configuration: FormaliteConfigurationParameters) {
    const { dossierObjectGroupSlice, dossierObjectGroupDataSourcesThunks } =
        createDossierObjectGroupSlice(configuration);
    return { dossierObjectGroupSlice, dossierObjectGroupDataSourcesThunks };
}

export function createDossierOpenSearchObjectGroupReducer(
    configuration: FormaliteConfigurationParameters,
) {
    const { dossierOpenSearchObjectGroupSlice, dossierOpenSearchObjectGroupDataSourcesThunks } =
        createDossierOpenSearchObjectGroupSlice(configuration);
    return { dossierOpenSearchObjectGroupSlice, dossierOpenSearchObjectGroupDataSourcesThunks };
}

export function createStatusReducer(configuration: FormaliteConfigurationParameters) {
    const { statusSlice, statusDataSourcesThunks } = createStatusSlice(configuration);
    return { statusSlice, statusDataSourcesThunks };
}

export function createDossierValidatorReducer(configuration: FormaliteConfigurationParameters) {
    const { dossierValidatorSlice, dossierValidatorDataSourcesThunks } =
        createDossierValidatorSlice(configuration);
    return { dossierValidatorSlice, dossierValidatorDataSourcesThunks };
}

export function createDossierTachesReducer(configuration: FormaliteConfigurationParameters) {
    const { dossierTachesSlice, dossierTachesDataSourcesThunks } =
        createDossierTachesSlice(configuration);
    return { dossierTachesSlice, dossierTachesDataSourcesThunks };
}
export function createDossierLogActionReducer(configuration: FormaliteConfigurationParameters) {
    const { dossierLogActionSlice, dossierLogActionDataSourcesThunks } =
        createDossierLogActionSlice(configuration);
    return { dossierLogActionSlice, dossierLogActionDataSourcesThunks };
}
export const baseFormalitySelector = (state: FormalityStateType) =>
    state[FORMALITES_REDUCER_NAME][FORMALITES_SLICE_NAME];

export function buildFormaliteRedux(configuration: FormaliteConfigurationParameters) {
    const formalitesConfiguration = new FormalitesConfiguration(configuration);

    const formaliteApi = new FormaliteApi(formalitesConfiguration);
    const formaliteOpenSearchApi = new FormaliteArticleOpenSearchApi(formalitesConfiguration);
    const dossierApi = new DossierApi(formalitesConfiguration);
    const prestationArticlesApi = new PrestationArticlesApi(formalitesConfiguration);
    const dossierOpenSearchApi = new DossierOpenSearchApi(formalitesConfiguration);
    const collectionApi = new CollectionDeDossierApi(formalitesConfiguration);
    const collectionOpenSearchApi = new CollectionOpenSearchApi(formalitesConfiguration);
    const fichiersDossierApi = new FichiersDossierApi(formalitesConfiguration);
    const formalisteApi = new FormalisteApi(formalitesConfiguration);
    const statusApi = new StatutApi(formalitesConfiguration);
    const prestationFormaliteApi = new PrestationApi(formalitesConfiguration);
    const messageApi = new MessageApi(formalitesConfiguration);
    const documentsApi = new TypeDeDocumentDesDossiersApi(formalitesConfiguration);
    const typeDocApi = new TypeDeDocumentApi(formalitesConfiguration);
    const dossierObjectGroupApi = new ObjectGroupsApi(formalitesConfiguration);
    const acomptesApi = new AcomptesApi(formalitesConfiguration);
    const reglementsApi = new ReglementsApi(formalitesConfiguration);
    const validationProcessApi = new ProcdureDeValidationDesDossiersApi(formalitesConfiguration);
    const validatorsApi = new ValideursDesDossiersApi(formalitesConfiguration);
    const notesApi = new NotesApi(formalitesConfiguration);
    const greffeApi = new GreffeApi(formalitesConfiguration);
    const dossierValidatorApi = new ValideursDesDossiersApi(formalitesConfiguration);
    const tachesEtRappelsDossierApi = new TachesEtRappelsDesDossierApi(formalitesConfiguration);
    const actionLoggerApi = new ActionLoggerApi(formalitesConfiguration);
    const dossierSignApi = new DossiersDeSignatureLectroniqueApi(formalitesConfiguration);
    const documentGedApi = new DocumentGedApi(formalitesConfiguration);

    const { orderSlice, orderDataSourcesThunks } = createOrderReducer(configuration);

    const { dossierFormaliteSlice, dossierFormaliteDataSourcesThunks } =
        createDossierFormaliteReducer(configuration);

    const { dossierOpenSearchSlice, dossierOpenSearchDataSourcesThunks } =
        createDossierOpenSearchReducer(configuration);

    const { dossierOpenSearchPrescriberSlice, dossierOpenSearchPrescriberDataSourcesThunks } =
        createDossierOpenSearchPrescriberReducer(configuration);

    const { orderOpenSearchSlice, orderOpenSearchDataSourcesThunks } =
        createOrderrOpenSearchReducer(configuration);

    const { formaliteSlice, formaliteDataSourcesThunks } = createFormaliteReducer(configuration);

    const { categoryFormaliteSlice, categoryFormaliteDataSourcesThunks } =
        createCategoriesFormaliteReducer(configuration);

    const { legalStatusSlice, legalStatusDataSourcesThunks } =
        createLegalStatusReducer(configuration);

    const { formaliteDocumentSlice, formaliteDocumentDataSourcesThunks } =
        createFormaliteDocumentReducer(configuration);

    const { formalisteSlice, formalisteDataSourcesThunks } = createFormalisteReducer(configuration);
    const { dossierStepSlice, dossierStepDataSourcesThunks } =
        createDossierStepReducer(configuration);
    const { dossierObjectGroupSlice, dossierObjectGroupDataSourcesThunks } =
        createDossierObjectGroupReducer(configuration);

    const { dossierOpenSearchObjectGroupSlice, dossierOpenSearchObjectGroupDataSourcesThunks } =
        createDossierOpenSearchObjectGroupReducer(configuration);
    const { statusSlice, statusDataSourcesThunks } = createStatusReducer(configuration);
    const { dossierValidatorSlice, dossierValidatorDataSourcesThunks } =
        createDossierValidatorReducer(configuration);
    const { dossierTachesSlice, dossierTachesDataSourcesThunks } =
        createDossierTachesReducer(configuration);

    const { dossierLogActionSlice, dossierLogActionDataSourcesThunks } =
        createDossierLogActionReducer(configuration);

    const combinedFormaliteReducer = combineReducers({
        [ORDERS_SLICE_NAME]: orderSlice.reducer,
        [DOSSIERS_FORMALITES_SLICE_NAME]: dossierFormaliteSlice.reducer,
        [DOSSIERS_OPEN_SEARCH_SLICE_NAME]: dossierOpenSearchSlice.reducer,
        [DOSSIERS_OPEN_SEARCH_PRESCRIBER_SLICE_NAME]: dossierOpenSearchPrescriberSlice.reducer,
        [ORDERS_OPEN_SEARCH_SLICE_NAME]: orderOpenSearchSlice.reducer,
        [FORMALITES_SLICE_NAME]: formaliteSlice.reducer,
        [CATEGORY_SLICE_NAME]: categoryFormaliteSlice.reducer,
        [LEGAL_STATUS_SLICE_NAME]: legalStatusSlice.reducer,
        [FORMALITE_DOCUMENT_SLICE_NAME]: formaliteDocumentSlice.reducer,
        [FORMALISTE_SLICE_NAME]: formalisteSlice.reducer,
        [STATUS_SLICE_NAME]: statusSlice.reducer,
        [DOSSIER_STEP_SLICE_NAME]: dossierStepSlice.reducer,
        [DOSSIER_OBJECT_GROUP_SLICE_NAME]: dossierObjectGroupSlice.reducer,
        [DOSSIERS_OPEN_SEARCH_OBJECT_GROUP_SLICE_NAME]: dossierOpenSearchObjectGroupSlice.reducer,
        [DOSSIER_VALIDATOR_SLICE_NAME]: dossierValidatorSlice.reducer,
        [DOSSIER_TACHES_SLICE_NAME]: dossierTachesSlice.reducer,
        [DOSSIER_LOG_ACTION_SLICE_NAME]: dossierLogActionSlice.reducer,
    });

    return {
        serviceConfigurationParameters: configuration,
        serviceConfiguration: formalitesConfiguration,
        reducer: combinedFormaliteReducer,

        apis: {
            formaliteApi,
            formaliteOpenSearchApi,
            dossierApi,
            prestationArticlesApi,
            dossierOpenSearchApi,
            collectionOpenSearchApi,
            collectionApi,
            fichiersDossierApi,
            formalisteApi,
            statusApi,
            prestationFormaliteApi,
            messageApi,
            documentsApi,
            acomptesApi,
            reglementsApi,
            typeDocApi,
            dossierObjectGroupApi,
            validationProcessApi,
            validatorsApi,
            notesApi,
            greffeApi,
            dossierValidatorApi,
            tachesEtRappelsDossierApi,
            actionLoggerApi,
            dossierSignApi,
            documentGedApi,
        },

        slices: {
            orderSlice,
            dossierFormaliteSlice,
            dossierOpenSearchSlice,
            dossierOpenSearchPrescriberSlice,
            formaliteSlice,
            legalStatusSlice,
            categoryFormaliteSlice,
            formaliteDocumentSlice,
            formalisteSlice,
            dossierStepSlice,
            dossierObjectGroupSlice,
            dossierOpenSearchObjectGroupSlice,
            statusSlice,
            dossierValidatorSlice,
            dossierTachesSlice,
            dossierLogActionSlice,
        },
        thunks: {
            orderDataSourcesThunks,
            dossierFormaliteDataSourcesThunks,
            dossierOpenSearchDataSourcesThunks,
            dossierOpenSearchPrescriberDataSourcesThunks,
            orderOpenSearchDataSourcesThunks,
            formaliteDataSourcesThunks,
            categoryFormaliteDataSourcesThunks,
            legalStatusDataSourcesThunks,
            formaliteDocumentDataSourcesThunks,
            formalisteDataSourcesThunks,
            dossierStepDataSourcesThunks,
            dossierObjectGroupDataSourcesThunks,
            statusDataSourcesThunks,
            dossierValidatorDataSourcesThunks,
            dossierTachesDataSourcesThunks,
            dossierLogActionDataSourcesThunks,
        },

        fetchItems: {
            getFormalites: formaliteDataSourcesThunks.main.getData,
            getDossierFormalite: dossierFormaliteDataSourcesThunks.main.getData,
            getOpenSearchDossiers: dossierOpenSearchDataSourcesThunks.main.getData,
            getOpenSearchDossiersPrescriber:
                dossierOpenSearchPrescriberDataSourcesThunks.main.getData,
            getOpenSearchCollections: orderOpenSearchDataSourcesThunks.main.getData,
            getAutocompleteDossierFormalite:
                dossierFormaliteDataSourcesThunks.autocompleteDossiersFormalites.getData,
            getFormalistes: formalisteDataSourcesThunks.main.getData,
            getDossierSteps: dossierStepDataSourcesThunks.main.getData,
            getObjectGroups: dossierObjectGroupDataSourcesThunks.main.getData,
            getDossierOpenSearchObjectGroups:
                dossierOpenSearchObjectGroupDataSourcesThunks.main.getData,
            getStatuses: statusDataSourcesThunks.main.getData,
            getDossierValidators: dossierValidatorDataSourcesThunks.main.getData,
            getDossierTaches: dossierTachesDataSourcesThunks.main.getData,
            getFormaliteDocuments: formaliteDocumentDataSourcesThunks.main.getData,
        },
        getItems: {
            selectOrder: orderDataSourcesThunks.main.getSelected,
            selectDossierFormalite: dossierFormaliteDataSourcesThunks.main.getSelected,
            selectOpenSearchDossier: dossierOpenSearchDataSourcesThunks.main.getSelected,
            selectOpenSearchDossierPrescriber:
                dossierOpenSearchPrescriberDataSourcesThunks.main.getSelected,
            selectFormality: formaliteDataSourcesThunks.main.getSelected,
            selectFormaliteDocument: formaliteDocumentDataSourcesThunks.main.getSelected,

            selectAutocompleteDossierFormalite:
                dossierFormaliteDataSourcesThunks.autocompleteDossiersFormalites.getSelected,
            selectFormaliste: formalisteDataSourcesThunks.main.getSelected,
            selectDossierStep: dossierStepDataSourcesThunks.main.getSelected,
            selectDossierObjectGroup: dossierStepDataSourcesThunks.main.getSelected,
            selectStatus: statusDataSourcesThunks.main.getSelected,
            selectDossierValidator: dossierValidatorDataSourcesThunks.main.getSelected,
            selectDossierTache: dossierTachesDataSourcesThunks.main.getSelected,
            selectLogAction: dossierLogActionDataSourcesThunks.main.getSelected,
        },

        models: {
            formality: buildFormalityModel(
                formaliteApi,
                formaliteOpenSearchApi,
                formaliteDataSourcesThunks,
            ),
        },
    };
}
